import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import {
  DEFAULT_BRANDING,
  DEFAULT_HERO_CTAS,
  DEFAULT_HOMEPAGE,
  DEFAULT_NAVIGATION,
  DEFAULT_NEWS,
} from '../constants/siteDefaults';
import {
  readSettingsLocalCache,
  writeSettingsLocalCache,
  SITE_SETTINGS_CACHE_KEY,
} from '../utils/settingsLocalCache';

const SETTINGS_DOC = 'site_settings';
const CURRENT_ID = 'current';

export const DEFAULT_SITE_SETTINGS = {
  branding: DEFAULT_BRANDING,
  navigation: DEFAULT_NAVIGATION,
  homepage: DEFAULT_HOMEPAGE,
  news: DEFAULT_NEWS,
};

let cachedSettings = null;
let cacheLoadedAt = 0;
const CACHE_TTL_MS = 60 * 1000;

const sortByOrder = (items = []) =>
  [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

const EXHIBITOR_NAV_CHILD = {
  id: 'conf-exhibitor',
  label: 'Exhibitor registration',
  path: '/register/exhibitor',
  visible: true,
  order: 3,
};

const EXHIBITOR_TERTIARY_LINK = {
  label: 'Exhibitor registration',
  path: '/register/exhibitor',
};

const ensureExhibitorNavLink = (navigation = []) =>
  navigation.map((item) => {
    if (item.id !== 'nav-conference') return item;
    const children = item.children || [];
    if (children.some((child) => child.path === '/register/exhibitor')) {
      return item;
    }
    return {
      ...item,
      children: sortByOrder([...children, EXHIBITOR_NAV_CHILD]),
    };
  });

const ensureExhibitorEventCardLink = (eventCards = []) =>
  eventCards.map((card) => {
    if (card.id !== 'conference') return card;
    const links = card.tertiary_links || [];
    if (links.some((link) => link.path === '/register/exhibitor')) {
      return card;
    }
    const packagesIndex = links.findIndex((link) => link.path === '/packages');
    const insertAt = packagesIndex >= 0 ? packagesIndex + 1 : links.length;
    return {
      ...card,
      tertiary_links: [
        ...links.slice(0, insertAt),
        EXHIBITOR_TERTIARY_LINK,
        ...links.slice(insertAt),
      ],
    };
  });

const mergeNavigation = (navigation = []) => {
  const source = navigation.length ? navigation : DEFAULT_NAVIGATION;
  return sortByOrder(
    ensureExhibitorNavLink(source).map((item) => ({
      ...item,
      children: sortByOrder(item.children || []),
    }))
  ).filter((item) => item.visible !== false);
};

const mergeNews = (news = {}) => ({
  ...DEFAULT_NEWS,
  ...news,
  items: sortByOrder(
    (news.items?.length ? news.items : DEFAULT_NEWS.items).filter((item) => item.visible !== false)
  ),
});

const mergeSettings = (data) => ({
  branding: { ...DEFAULT_BRANDING, ...(data?.branding || {}) },
  navigation: mergeNavigation(data?.navigation),
  homepage: {
    ...DEFAULT_HOMEPAGE,
    ...(data?.homepage || {}),
    what_we_do_cards: data?.homepage?.what_we_do_cards?.length
      ? data.homepage.what_we_do_cards
      : DEFAULT_HOMEPAGE.what_we_do_cards,
    event_cards: ensureExhibitorEventCardLink(
      data?.homepage?.event_cards?.length
        ? data.homepage.event_cards
        : DEFAULT_HOMEPAGE.event_cards
    ),
    hero_ctas: data?.homepage?.hero_ctas?.length
      ? data.homepage.hero_ctas
      : DEFAULT_HERO_CTAS,
  },
  news: mergeNews(data?.news),
});

/** Full settings including hidden nav/news items (for admin editor) */
export const mergeSiteSettingsRaw = (data) => ({
  branding: { ...DEFAULT_BRANDING, ...(data?.branding || {}) },
  navigation: sortByOrder(
    ensureExhibitorNavLink(
      data?.navigation?.length ? data.navigation : DEFAULT_NAVIGATION
    ).map((item) => ({
      ...item,
      children: sortByOrder(item.children || []),
    }))
  ),
  homepage: {
    ...DEFAULT_HOMEPAGE,
    ...(data?.homepage || {}),
    what_we_do_cards: data?.homepage?.what_we_do_cards?.length
      ? data.homepage.what_we_do_cards
      : DEFAULT_HOMEPAGE.what_we_do_cards,
    event_cards: ensureExhibitorEventCardLink(
      data?.homepage?.event_cards?.length
        ? data.homepage.event_cards
        : DEFAULT_HOMEPAGE.event_cards
    ),
    hero_ctas: data?.homepage?.hero_ctas?.length
      ? data.homepage.hero_ctas
      : DEFAULT_HERO_CTAS,
  },
  news: {
    ...DEFAULT_NEWS,
    ...(data?.news || {}),
    items: sortByOrder(data?.news?.items?.length ? data.news.items : DEFAULT_NEWS.items),
  },
});

export const getSiteSettingsSync = () =>
  cachedSettings ? mergeSettings(cachedSettings) : mergeSettings(DEFAULT_SITE_SETTINGS);

export const getSiteSettings = async ({ forceRefresh = false } = {}) => {
  const now = Date.now();
  if (!forceRefresh && cachedSettings && now - cacheLoadedAt < CACHE_TTL_MS) {
    return mergeSettings(cachedSettings);
  }

  if (!forceRefresh) {
    const localCached = readSettingsLocalCache(SITE_SETTINGS_CACHE_KEY);
    if (localCached) {
      cachedSettings = localCached;
      cacheLoadedAt = now;
      return mergeSettings(cachedSettings);
    }
  }

  try {
    const snap = await getDoc(doc(db, SETTINGS_DOC, CURRENT_ID));
    if (snap.exists()) {
      cachedSettings = snap.data();
      cacheLoadedAt = now;
      writeSettingsLocalCache(SITE_SETTINGS_CACHE_KEY, cachedSettings);
      return mergeSettings(cachedSettings);
    }
  } catch (error) {
    console.error('Error loading site settings:', error);
  }

  return mergeSettings(DEFAULT_SITE_SETTINGS);
};

export const getSiteSettingsForAdmin = async () => {
  try {
    const snap = await getDoc(doc(db, SETTINGS_DOC, CURRENT_ID));
    if (snap.exists()) {
      return mergeSiteSettingsRaw(snap.data());
    }
  } catch (error) {
    console.error('Error loading site settings for admin:', error);
  }
  return mergeSiteSettingsRaw(DEFAULT_SITE_SETTINGS);
};

export const upsertSiteSettings = async (settings) => {
  const payload = {
    ...mergeSiteSettingsRaw(settings),
    updated_at: serverTimestamp(),
  };
  await setDoc(doc(db, SETTINGS_DOC, CURRENT_ID), payload, { merge: true });
  cachedSettings = payload;
  cacheLoadedAt = Date.now();
  writeSettingsLocalCache(SITE_SETTINGS_CACHE_KEY, payload);
  return mergeSiteSettingsRaw(payload);
};

export const moveItem = (items, index, direction) => {
  const next = [...items];
  const target = index + direction;
  if (target < 0 || target >= next.length) return items;
  [next[index], next[target]] = [next[target], next[index]];
  return next.map((item, i) => ({ ...item, order: i }));
};
